/**
 * cart-auto-add.js
 *
 * Auto-ajoute des produits au panier selon des règles configurées dans les
 * réglages globaux du thème (groupe « Panier »), et les retire quand la
 * condition n'est plus remplie. Le(s) produit(s) auto sont placés en bas.
 *
 * Principe : RÉCONCILIATION PAR ÉTAT DÉSIRÉ (idempotent).
 *   panier + règles -> ensemble {variantId: qty} qui DOIT être auto-présent
 *   diff vs ce qui EST auto-présent (lignes taguées via properties._auto_rule)
 *   -> applique uniquement le delta. Si désiré == actuel : NO-OP, aucune requête.
 *
 * Anti-conflit / anti-boucle :
 *   - Toutes nos écritures passent par UNE seule chaîne de promesses sérialisée
 *     (queue) : jamais deux /cart/* en parallèle de notre fait.
 *   - On attend que le panier soit STABLE (le thème a fini son propre cycle)
 *     avant de muter : on relit /cart.js, on compare, et on n'agit que si besoin.
 *   - On ne republie PAS cartUpdate (ça relançait le thème en cascade). À la
 *     place on rafraîchit nous-mêmes les sections du panier (drawer + bulle).
 *   - Source-tag + idempotence par diff : un re-déclenchement sans changement
 *     réel ne produit aucune requête.
 *
 * Dépendances thème (Dawn) : window.routes, fetchConfig() (global.js),
 * subscribe/publish + PUB_SUB_EVENTS (pubsub.js / constants.js).
 */
(function () {
  'use strict';

  var SOURCE = 'cart-auto-add';
  var AUTO_PROP = '_auto_rule';
  var DEBOUNCE_MS = 400; // laisse le thème terminer son propre change.js + re-render

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      var txt = (el.textContent || '').trim();
      return txt ? JSON.parse(txt) : null;
    } catch (e) {
      console.warn('[cart-auto-add] JSON invalide pour #' + id, e);
      return null;
    }
  }

  var RULES = readJSON('cart-auto-add-rules') || [];
  var COLLECTIONS = readJSON('cart-auto-add-collections') || {};
  if (!Array.isArray(RULES) || RULES.length === 0) return;

  function cartUrl() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return base.replace(/\/$/, '') + '.js';
  }

  function postConfig(body) {
    var cfg = typeof fetchConfig === 'function'
      ? fetchConfig('json')
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return Object.assign({}, cfg, { body: JSON.stringify(body) });
  }

  function getCart() {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isAuto(item) {
    return !!(item && item.properties && item.properties[AUTO_PROP]);
  }

  // Signature stable du panier (variant:qty:auto), pour détecter un changement réel.
  function signature(cart) {
    return (cart.items || [])
      .map(function (it) { return it.variant_id + ':' + it.quantity + ':' + (isAuto(it) ? 'A' : 'M'); })
      .join('|');
  }

  // ----- Évaluation des déclencheurs (en excluant les lignes auto) -----

  function manualItems(cart) {
    return (cart.items || []).filter(function (it) { return !isAuto(it); });
  }

  function qtyOfProduct(cart, productId) {
    if (!productId) return 0;
    return manualItems(cart).reduce(function (sum, it) {
      return it.product_id === productId ? sum + it.quantity : sum;
    }, 0);
  }

  function qtyInCollection(cart, handle) {
    var map = COLLECTIONS[handle];
    if (!map || !Array.isArray(map.productIds)) return 0;
    var ids = map.productIds;
    return manualItems(cart).reduce(function (sum, it) {
      return ids.indexOf(it.product_id) !== -1 ? sum + it.quantity : sum;
    }, 0);
  }

  function manualItemCount(cart) {
    return manualItems(cart).reduce(function (sum, it) { return sum + it.quantity; }, 0);
  }

  // Quantité cumulée (lignes manuelles) des variantes listées.
  function qtyOfVariants(cart, variantIds) {
    if (!Array.isArray(variantIds) || !variantIds.length) return 0;
    return manualItems(cart).reduce(function (sum, it) {
      return variantIds.indexOf(it.variant_id) !== -1 ? sum + it.quantity : sum;
    }, 0);
  }

  // Déclenché si AU MOINS un palier atteint sa quantité (logique OU).
  function anyTierMet(tiers, cart) {
    if (!Array.isArray(tiers)) return false;
    return tiers.some(function (t) {
      return qtyOfVariants(cart, t.variantIds) >= (t.qty || 1);
    });
  }

  function manualTotalCents(cart) {
    var autoCents = (cart.items || []).reduce(function (sum, it) {
      return isAuto(it) ? sum + (it.final_line_price != null ? it.final_line_price : it.line_price || 0) : sum;
    }, 0);
    return (cart.total_price || 0) - autoCents;
  }

  function ruleTriggered(rule, cart) {
    switch (rule.triggerType) {
      case 'cart_total':
        return manualTotalCents(cart) >= (rule.thresholdAmount || 0);
      case 'product_qty':
        return rule.triggerProductId != null && qtyOfProduct(cart, rule.triggerProductId) >= (rule.thresholdQty || 1);
      case 'collection_qty':
        return rule.triggerCollectionHandle != null && qtyInCollection(cart, rule.triggerCollectionHandle) >= (rule.thresholdQty || 1);
      case 'item_count':
        return manualItemCount(cart) >= (rule.thresholdQty || 1);
      case 'variant_qty_any':
        return anyTierMet(rule.tiers, cart);
      default:
        return false;
    }
  }

  // ----- État désiré / actuel / diff -----

  function computeDesired(cart) {
    var desired = {};
    RULES.forEach(function (rule) {
      if (!ruleTriggered(rule, cart)) return;
      var targets = [];
      if (rule.targetType === 'product') {
        if (rule.targetVariantId != null) targets.push({ id: rule.targetVariantId, quantity: 1 });
      } else if (rule.targetType === 'collection') {
        var map = COLLECTIONS[rule.targetCollectionHandle];
        if (map && Array.isArray(map.addTargets)) targets = map.addTargets.slice();
      }
      targets.forEach(function (t) {
        if (t.id == null) return;
        var key = String(t.id);
        var qty = t.quantity || 1;
        if (!desired[key] || qty > desired[key].quantity) {
          desired[key] = { quantity: qty, ruleId: rule.id };
        }
      });
    });
    return desired;
  }

  function currentAuto(cart) {
    var current = {};
    (cart.items || []).forEach(function (it) {
      if (!isAuto(it)) return;
      current[String(it.variant_id)] = { quantity: it.quantity, key: it.key };
    });
    return current;
  }

  function computeDiff(cart) {
    var desired = computeDesired(cart);
    var current = currentAuto(cart);
    var adds = [];
    var removes = []; // line keys

    Object.keys(desired).forEach(function (vid) {
      var want = desired[vid];
      var have = current[vid];
      if (!have) {
        adds.push({ id: parseInt(vid, 10), quantity: want.quantity, ruleId: want.ruleId });
      } else if (have.quantity !== want.quantity) {
        removes.push(have.key);
        adds.push({ id: parseInt(vid, 10), quantity: want.quantity, ruleId: want.ruleId });
      }
    });
    Object.keys(current).forEach(function (vid) {
      if (!desired[vid]) removes.push(current[vid].key);
    });
    return { adds: adds, removes: removes };
  }

  // ----- Application (toujours appelée DANS la queue sérialisée) -----

  function removeLine(key, sections) {
    var body = { id: key, quantity: 0 };
    if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
    return fetch(window.routes.cart_change_url, postConfig(body))
      .then(function (r) { return r.json(); })
      .catch(function (e) { console.warn('[cart-auto-add] retrait échoué', key, e); return null; });
  }

  // Retraits séquentiels (clés stables). On demande les `sections` au DERNIER
  // retrait seulement, et on renvoie son état rendu final.
  function removeSeq(keys, sections) {
    var last = null;
    return keys.reduce(function (p, key, i) {
      return p.then(function () {
        var withSections = (i === keys.length - 1) ? sections : null;
        return removeLine(key, withSections).then(function (state) { last = state; });
      });
    }, Promise.resolve()).then(function () { return last; });
  }

  function addItems(adds, sections) {
    if (!adds.length) return Promise.resolve(null);
    var items = adds.map(function (a) {
      var props = {};
      props[AUTO_PROP] = a.ruleId;
      return { id: a.id, quantity: a.quantity, properties: props };
    });
    var body = { items: items };
    if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
    return fetch(window.routes.cart_add_url, postConfig(body))
      .then(function (r) { return r.json(); })
      .catch(function (e) { console.warn('[cart-auto-add] ajout échoué', e); return null; });
  }

  // ----- Rendu : 100% délégué au thème -----
  // On ne reconstruit AUCUN HTML à la main. On demande à /cart/*.js les `sections`
  // que le custom element natif veut, et on applique l'état EXACTEMENT comme le
  // thème le fait dans cart.js:180-194 (toggle is-empty + re-render via
  // getSectionsToRender du host). Le thème reste seul responsable de l'affichage
  // (drawer vide, bulle, footer, animations).

  // Host natif qui pilote le rendu : drawer si présent, sinon page panier.
  function renderHost() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  // Ids de sections à demander à /cart/*.js (format attendu par le host natif).
  function sectionsParam(host) {
    if (!host || typeof host.getSectionsToRender !== 'function') return null;
    var ids = host.getSectionsToRender().map(function (s) { return s.section; }).filter(Boolean);
    return ids.length ? ids : null;
  }

  // Applique parsedState au DOM en répliquant fidèlement cart.js:180-194.
  function applyState(parsedState) {
    if (!parsedState || parsedState.errors) return;
    var empty = parsedState.item_count === 0;

    // 1) Bascule de l'état "vide" — ce que l'ancien code maison oubliait.
    var drawer = document.querySelector('cart-drawer');
    var mainItems = document.getElementById('main-cart-items');
    var mainFooter = document.getElementById('main-cart-footer');
    if (drawer) drawer.classList.toggle('is-empty', empty);
    if (mainItems) mainItems.classList.toggle('is-empty', empty);
    if (mainFooter) mainFooter.classList.toggle('is-empty', empty);

    // 2) Re-render via le getSectionsToRender NATIF du host (mêmes sélecteurs
    //    que le thème, donc même comportement vide/plein).
    var host = renderHost();
    if (host && parsedState.sections && typeof host.getSectionsToRender === 'function') {
      host.getSectionsToRender().forEach(function (section) {
        var el = document.getElementById(section.id);
        if (!el) return;
        var target = (section.selector && el.querySelector(section.selector)) || el;
        var html = parsedState.sections[section.section];
        if (html == null) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var src = section.selector ? parsed.querySelector(section.selector) : parsed.body;
        target.innerHTML = src ? src.innerHTML : html;
      });
    }

    // 3) Ré-init des widgets du thème (mêmes hooks que cart.js / cart-drawer.js).
    setTimeout(function () {
      if (typeof window.initUpsellCarousel === 'function') window.initUpsellCarousel();
      if (typeof window.initUpsellDiscounts === 'function') window.initUpsellDiscounts();
      if (typeof window.initCartVariantSelectors === 'function') window.initCartVariantSelectors();
    }, 0);
  }

  // ----- Réconciliation sérialisée -----

  var chain = Promise.resolve(); // queue globale : une seule écriture à la fois
  var queued = false;            // un reconcile est déjà en attente dans la chaîne
  var debounceTimer = null;
  var lastSeenSig = null;

  function reconcileOnce() {
    return getCart().then(function (cart) {
      var diff = computeDiff(cart);

      if (diff.adds.length === 0 && diff.removes.length === 0) {
        lastSeenSig = signature(cart);
        return null; // NO-OP total : la garde de signature éteint toute boucle.
      }

      var sections = sectionsParam(renderHost());

      // On demande les sections à la DERNIÈRE écriture seulement (retrait final
      // si rien à ajouter, sinon l'ajout qui se fait en dernier). On applique CET
      // état rendu — produit par le thème, pas par nous.
      return removeSeq(diff.removes, diff.adds.length ? null : sections)
        .then(function (removeState) {
          return diff.adds.length ? addItems(diff.adds, sections) : removeState;
        })
        .then(function (finalState) { applyState(finalState); })
        .then(function () { return getCart(); })
        .then(function (fresh) { lastSeenSig = signature(fresh); });
    });
  }

  function enqueueReconcile() {
    if (queued) return; // déjà un reconcile en file -> il verra l'état final
    queued = true;
    chain = chain
      .then(function () { queued = false; return reconcileOnce(); })
      .catch(function (e) { queued = false; console.warn('[cart-auto-add] réconciliation échouée', e); });
  }

  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(enqueueReconcile, DEBOUNCE_MS);
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === SOURCE) return; // jamais sur nos propres events
        scheduleReconcile();
      });
    }
    // Passage initial : applique les règles même sans action (idempotent au reload).
    scheduleReconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
